import { getCopilotChatDrawerState } from '../store/store';
import {
    MINIMUM_COPILOT_CHAT_DRAWER_WIDTH,
    NARROWEST_CANVAS_MODE_BREAKPOINT,
    MAX_COPILOT_CHAT_DRAWER_WIDTH_CANVAS_RATIO,
} from '../utils/copilotChatDrawerConstants';
import { getCanvasContentMinimumWidth } from '../utils/getCanvasContentMinimumWidth';

function isNarrowestCanvasMode(appCanvasWidth: number): boolean {
    return appCanvasWidth < NARROWEST_CANVAS_MODE_BREAKPOINT;
}

// Initial state uses 0 until ResizeObserver reports the real canvas width, so
// 0 (or no state at all) means "not measured yet".
function getMeasuredAppCanvasWidth(targetWindow: Window): number | undefined {
    const appCanvasWidth = getCopilotChatDrawerState(targetWindow)?.appCanvasWidth;
    return appCanvasWidth !== undefined && appCanvasWidth > 0 ? appCanvasWidth : undefined;
}

export function hasCopilotChatDrawerCanvasWidthMeasurement(targetWindow: Window): boolean {
    return getMeasuredAppCanvasWidth(targetWindow) !== undefined;
}

export function shouldCopilotChatDrawerUseFullCanvas(targetWindow: Window): boolean {
    const appCanvasWidth = getMeasuredAppCanvasWidth(targetWindow);
    return appCanvasWidth !== undefined && isNarrowestCanvasMode(appCanvasWidth);
}

/**
 * This function computes the final intended width of the Copilot Chat Drawer, and
 * is where the logic for all the special cases for the Copilot Chat Drawer width is handled.
 *
 * There are "3 Stages" to this repsonsiveness.
 *
 * Stage 1: "Wide Screens".
 *  - We clamp between a minimum and maximum width.
 *  - The minimum width is MINIMUM_COPILOT_CHAT_DRAWER_WIDTH (380px).
 *  - The maximum width is 60% of the appCanvasWidth.
 *  - We use userPreferredWidth if it's between those two values, otherwise clamp.
 *
 * Stage 2: "Narrower Screens".
 *  - At a certain point, ML/RP will be at its optimal minimum of approx. 380px.
 *  - We don't want ML/RP to shrink farther, so we only shrink Drawer now.
 *  - Thus, logic is to use Stage 1's width, unless that would cause content to be < optimal minimum.
 *  - If so, we adjust to make the content always at least that minimum width.
 *
 * Stage 3: "Narrowest Screens".
 *  - At this point, we will either show ML/RP or show Copilot Chat Drawer. They become mutually exclusive.
 *  - Thus, we deploy a "show/hide" instead of both sharing the screen.
 */
export function getCopilotChatDrawerTrueWidth(targetWindow: Window): number {
    const state = getCopilotChatDrawerState(targetWindow);
    if (!state) {
        return 0;
    }

    const userPreferredWidth = state.userPreferredWidth;
    const appCanvasWidth = state.appCanvasWidth;

    // [Stage 3]
    // If canvas width is below the narrowest mode breakpoint, then we are in a state
    // where the chat will just be the width of the canvas to have it take up the entire space.
    if (isNarrowestCanvasMode(appCanvasWidth)) {
        return appCanvasWidth;
    }

    // Calculate clamped drawer width - e.g. clamp(minWidth, userPreferredWidth, maxWidth)
    const maxWidth = appCanvasWidth * MAX_COPILOT_CHAT_DRAWER_WIDTH_CANVAS_RATIO;
    const minContentWidth = getCanvasContentMinimumWidth();
    const clampedDrawerWidth = Math.max(
        MINIMUM_COPILOT_CHAT_DRAWER_WIDTH,
        Math.min(userPreferredWidth, maxWidth)
    );

    // [Stage 2]
    // Check if our proposed clampedDrawerWidth would leave enough room for the content area (ML/RP)
    if (appCanvasWidth - clampedDrawerWidth < minContentWidth) {
        // If not enough room, return the width that leaves enough room for the content area
        return appCanvasWidth - minContentWidth;
    }

    // [Stage 1]: Just use the clamped width
    return clampedDrawerWidth;
}
